import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  PlusCircleIcon,
  TrashIcon,
  PencilIcon,
  WrenchIcon,
  CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import Button from "aldoo-ra/Button";
import { GetCMSContent } from "aldoo-ra/CMS/api";
import ContentEditor from "aldoo-ra/CMS/content-editor/index";
import Controller from "aldoo-ra/CMS/controllers/cms-controller";
import RestoreDialog from "aldoo-ra/CMS/dialogs/restore-dialog";
import TypeSelectorDialog from "aldoo-ra/CMS/dialogs/type-selector-dialog";
import TypeResolver from "aldoo-ra/CMS/type-resolver";
import DataTable from "aldoo-ra/DataTable";
import Modal from "aldoo-ra/Modal";
import { showNotification } from "aldoo-ra/Notification";
import { showPromptDialog } from "aldoo-ra/PromptDialog";
import Typography from "aldoo-ra/Typography";
import DropDownSelect from "aldoo-ra/DropdownSelect";
import Tooltip from "aldoo-ra/Tooltip";
import ImagePreview from "aldoo-ra/CMS/preview/image-preview";
import { filename } from "aldoo-ra/CMS/url";
import { getPreSaveFormatter } from "aldoo-ra/CMS/content-pre-save-formatter";
import DateFilterDropdown from "aldoo-ra/DateFilterDropdown";
import FilterPanel from "aldoo-ra/Admin/Updates/controller/FilterPanel";
import Checkbox from "aldoo-ra/Checkbox";
import moment from "moment";
import { previewTitle } from "aldoo-ra/CMS/preview";
import { buildCustomMatchFilters } from "aldoo-ra/CMS/utils/buildCustomMatchFilters";
import { pluginRegistry, createPluginContext, PluginDialog } from "aldoo-ra/CMS/plugins/index.jsx";
import ProgressDialog from "aldoo-ra/ProgressDialog";
import EnvironmentContentInstallDialog from "aldoo-ra/Admin/Updates/Environments/EnvironmentContentInstallDialog";

export default function ContentBrowser({
  // Content configuration
  contentType: externalContentType,
  contentTitle = "Content",

  // Feature flags
  allowTypeSelection = true,
  showPreview = true,
  // Column configuration
  baseColumns = ["edit", "delete"],
  additionalColumns = [],
  // Search configuration
  searchFields = "content.name content.title content.id content.label content.type",
  initialSearchTerm = "",
  initialDateRange = null,
  // Filter configuration
  additionalFilters = {},
  filterDataModel = [], // Data model for enhanced filter suggestions
  // Sort configuration
  sort = { createdAt: -1 }, // Default sort by creation date descending
  // Default field values for new content items
  defaultFieldValues = {},
  // Custom header controls
  customHeaderControls,
  // Add button text
  addButtonText = "Add",
  // External add dialog trigger
  externalAddTrigger = false,
  // External add dialog close callback
  onAddDialogClose,
  // External restore dialog trigger
  externalRestoreTrigger = false,
  // External restore dialog close callback
  onRestoreDialogClose,
  // Show restore button in headerControls
  showRestoreButton = true,
  // Style overrides for DataTable
  styleOverrides = {},
  // External refresh trigger
  externalRefreshTrigger = 0,
  // Local user preferences key for persisting table settings
  localUserPreferencesKey = null,
  // Validation
  customValidators,
  // This is a function that takes the content object right before it's saved
  // and can make changes to it before it's saved.
  // That's a nice place to plug-in some auto field population logic.
  onPreSaveFormatter,
  // This is an async function that gets called before deleting content
  // Should return { canDelete: boolean, message?: string, releases?: array }
  // If canDelete is false, deletion is blocked and message is shown
  onPreDeleteCheck,
  // Event handlers
  onContentTypeChange,
  // New filter change callback - gets triggered whenever filter changes
  onFilterChange,
  // Search change callback
  onSearchChange,
  // Date range change callback
  onDateRangeChange,
}) {
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [contentType, setContentType] = useState(null);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(null);
  const [dateFilter, setDateFilter] = useState(() => {
    // Initialize from URL if provided
    if (initialDateRange) {
      return initialDateRange
    }
    return { type: "preset", value: null } // Lifetime by default
  });
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const initialLoadRef = useRef(true);
  const [selectedContent, setSelectedContent] = useState([]);
  const [selectedContentData, setSelectedContentData] = useState([]);
  const [activePlugin, setActivePlugin] = useState(null);
  const [showPluginDialog, setShowPluginDialog] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progressData, setProgressData] = useState({ current: 0, total: 0, message: '' });
  const [showInstallDialog, setShowInstallDialog] = useState(false);

  // Update searchTerm when initialSearchTerm prop changes (e.g., from URL)
  useEffect(() => {
    if (searchTerm !== initialSearchTerm) {
      setSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm]);

  // Helper function to extract default values from ContentType field definitions
  const getDefaultFieldValues = (contentType) => {
    if (!contentType?.fields) return {};
    
    const defaults = {};
    contentType.fields.forEach(field => {
      if (field.default !== undefined && field.default !== null && field.default !== "") {
        // Handle case mismatch by checking if default exists in options
        if (field.options && Array.isArray(field.options)) {
          // Find exact match or case-insensitive match - handle both string and object options
          const exactMatch = field.options.some(opt => {
            if (typeof opt === 'string') {
              return opt === field.default
            }
            if (typeof opt === 'object' && opt.value) {
              return opt.value === field.default
            }
            return false
          });
          if (exactMatch) {
            defaults[field.id] = field.default;
          } else {
            // Try case-insensitive match - handle both string and object options
            const caseMatch = field.options.find(opt => {
              // Only do case-insensitive comparison if field.default is a string
              if (typeof field.default !== 'string') return false
              
              // Handle string options
              if (typeof opt === 'string') {
                return opt.toLowerCase() === field.default.toLowerCase()
              }
              // Handle object options with value property
              if (typeof opt === 'object' && opt.value && typeof opt.value === 'string') {
                return opt.value.toLowerCase() === field.default.toLowerCase()
              }
              return false
            });
            if (caseMatch) {
              defaults[field.id] = caseMatch;
            }
          }
        } else {
          defaults[field.id] = field.default;
        }
      }
    });
    return defaults;
  };
  const [customFilters, setCustomFilters] = useState([]);
  const isInitialMount = useRef(true);
  const lastExternalValue = useRef(null);
  const availableTypesList = useRef([]);
  const prevContentType = useRef(null);

  const controller = Controller();
  const { getContentType, availableTypeNames } = TypeResolver();

  // Store available types for reference
  useEffect(() => {
    const types = availableTypeNames().map((type) => ({
      value: type,
      name: type,
    }));
    availableTypesList.current = types;
  }, []);

  // Convert various type formats to dropdown format
  const formatTypeForDropdown = (type) => {
    if (!type) return null;

    if (typeof type === "string") {
      // Single type as string
      return [{ value: type, name: type }];
    } else if (Array.isArray(type)) {
      if (type.length === 0) return [];

      // Check if the first item is already in the correct format
      if (
        typeof type[0] === "object" &&
        type[0] !== null &&
        "value" in type[0]
      ) {
        return type;
      }

      // Convert array of strings to array of objects
      return type.map((t) => ({
        value: String(t),
        name: String(t),
      }));
    }

    return null;
  };

  // Helper function to compare external value with current state
  const hasExternalTypeChanged = (newExternalType) => {
    // Check if both are null/undefined
    if (!newExternalType && !lastExternalValue.current) {
      return false;
    }

    // Check if one is null/undefined but not the other
    if (!newExternalType || !lastExternalValue.current) {
      return true;
    }

    // Compare string values
    if (
      typeof newExternalType === "string" &&
      typeof lastExternalValue.current === "string"
    ) {
      return newExternalType !== lastExternalValue.current;
    }

    // Compare arrays
    if (
      Array.isArray(newExternalType) &&
      Array.isArray(lastExternalValue.current)
    ) {
      if (newExternalType.length !== lastExternalValue.current.length) {
        return true;
      }

      // Sort arrays for consistent comparison
      const sortedNew = [...newExternalType].sort();
      const sortedLast = [...lastExternalValue.current].sort();

      return !sortedNew.every((val, idx) => {
        if (typeof val === "object" && val !== null && "value" in val) {
          return (
            val.value ===
            (typeof sortedLast[idx] === "object" &&
            sortedLast[idx] !== null &&
            "value" in sortedLast[idx]
              ? sortedLast[idx].value
              : sortedLast[idx])
          );
        }
        return val === sortedLast[idx];
      });
    }

    // Different types, considered changed
    return true;
  };

  // Update contentType when externalContentType changes
  useEffect(() => {
    if (
      externalContentType !== undefined &&
      hasExternalTypeChanged(externalContentType)
    ) {
      // Update the last external value
      lastExternalValue.current = Array.isArray(externalContentType)
        ? [...externalContentType]
        : externalContentType;

      // Format for dropdown
      const formattedType = formatTypeForDropdown(externalContentType);

      // Update state and trigger data refresh
      setContentType(formattedType);
      setRefreshKey((prev) => prev + 1);
    }
  }, [externalContentType]);

  // Listen for content changes from controller
  useEffect(() => {
    controller.onRefreshContents(() => setRefreshKey((prev) => prev + 1));
  }, []);

  // Handle external add trigger
  useEffect(() => {
    if (externalAddTrigger) {
      addNewContent();
    }
  }, [externalAddTrigger]);

  // Handle external restore trigger
  useEffect(() => {
    if (externalRestoreTrigger) {
      setOpenRestoreDialog(true);
      // Call external close callback if provided
      if (onRestoreDialogClose) {
        onRestoreDialogClose();
      }
    }
  }, [externalRestoreTrigger]);

  // Refresh data table when date filter changes
  useEffect(() => {
    setRefreshKey((prev) => prev + 1);
  }, [
    dateFilter.type,
    dateFilter.value,
    dateFilter.range?.startDate,
    dateFilter.range?.endDate,
  ]);

  // Handle date filter changes
  const handleDateFilterChange = (newDateFilter) => {
    setDateFilter(newDateFilter);
    // Notify parent if callback exists
    if (onDateRangeChange) {
      onDateRangeChange(newDateFilter);
    }
  };

  // Track contentType changes to call onFilterChange
  useEffect(() => {
    // Skip initial render
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevContentType.current = contentType;
      return;
    }

    // Call onFilterChange if the filter changed and the callback exists
    if (onFilterChange && prevContentType.current !== contentType) {
      // Get the normalized filter value for the callback
      let filterValue = null;

      if (contentType) {
        if (Array.isArray(contentType) && contentType.length === 0) {
          filterValue = null;
        } else if (Array.isArray(contentType) && contentType.length === 1) {
          filterValue = contentType[0].value;
        } else if (Array.isArray(contentType)) {
          filterValue = contentType.map((t) => t.value);
        } else {
          filterValue = contentType;
        }
      }

      // Call the filter change callback
      onFilterChange({
        type: filterValue,
        filter: getTypeFilter(),
      });
    }

    // Update previous value
    prevContentType.current = contentType;
  }, [contentType, onFilterChange]);

  // Notify parent of contentType changes
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!onContentTypeChange || contentType === undefined) {
      return;
    }

    // Convert to the appropriate format for the parent
    let parentType;
    if (
      !contentType ||
      (Array.isArray(contentType) && contentType.length === 0)
    ) {
      parentType = null;
    } else if (Array.isArray(contentType) && contentType.length === 1) {
      parentType = contentType[0].value;
    } else if (Array.isArray(contentType)) {
      parentType = contentType.map((t) => t.value);
    } else {
      parentType = contentType;
    }

    // Only notify if there's a real change
    const hasChanged = hasExternalTypeChanged(parentType);
    if (hasChanged) {
      lastExternalValue.current = Array.isArray(parentType)
        ? [...parentType]
        : parentType;
      onContentTypeChange(parentType);
    }
  }, [contentType, onContentTypeChange]);

  // Helper function to check if filter has invalid values
  const hasInvalidValues = (filter) => {
    if (!filter) return false;
    if (!Array.isArray(filter)) return false;

    return filter.some(
      (item) =>
        typeof item === "object" &&
        item !== null &&
        "value" in item &&
        typeof item.value === "string" &&
        item.value.includes("=")
    );
  };

  // Get filter for database query
  const getTypeFilter = () => {
    // If contentType is null or empty array, return "any type" filter
    if (
      !contentType ||
      (Array.isArray(contentType) && contentType.length === 0) ||
      hasInvalidValues(contentType)
    ) {
      return { $exists: true };
    }

    // If contentType is set (from dropdown)
    if (Array.isArray(contentType)) {
      if (contentType.length === 1) {
        // Single selection - return just the value
        return contentType[0].value;
      }
      // Multiple selection - use $in operator with values
      return { $in: contentType.map((t) => t.value) };
    }

    // Default case - show any type
    return { $exists: true };
  };

  const resolveAvailableTypeNames = () => {
    return availableTypeNames()
      .map((type) => ({
        value: type,
        name: type,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // Handle delete action
  const deleteInstanceAction = async (field) => {
    const { name, label } = field.content || {};

    // Check if onPreDeleteCheck is provided
    if (onPreDeleteCheck) {
      try {
        const checkResult = await onPreDeleteCheck(field);

        if (!checkResult.canDelete) {
          // Show warning dialog with custom message
          showPromptDialog({
            title: `Cannot Delete Template`,
            message: checkResult.customMessage || (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-admin_warning/10 flex items-center justify-center">
                    <span className="text-2xl">⚠️</span>
                  </div>
                  <div className="flex-1">
                    <Typography variant="admin_txt1" className="font-semibold mb-2 text-admin_text">
                      {checkResult.message || 'This template is in use'}
                    </Typography>
                    {checkResult.releases && checkResult.releases.length > 0 && (
                      <div className="mt-3">
                        <Typography variant="admin_txt2_dim" className="mb-2">
                          Active in these releases:
                        </Typography>
                        <ul className="list-disc list-inside space-y-1">
                          {checkResult.releases.slice(0, 5).map((release, index) => (
                            <li key={index} className="text-admin_text text-sm">
                              {release.name} ({release.status})
                            </li>
                          ))}
                          {checkResult.releases.length > 5 && (
                            <li className="text-admin_text_secondary text-sm italic">
                              + {checkResult.releases.length - 5} more release{checkResult.releases.length - 5 !== 1 ? 's' : ''}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ),
            confirmText: "OK",
            showNoBtn: false,
            onConfirm: () => {
              // Just close the dialog
            },
          });
          return;
        }
      } catch (error) {
        console.error('Error in pre-delete check:', error);
        showNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to check if template can be deleted',
        });
        return;
      }
    }

    // Show standard delete confirmation
    showPromptDialog({
      title: `Confirm Delete`,
      message: `Are you sure you want to delete ${
        label || name || "this content"
      }?`,
      confirmText: "Delete",
      onConfirm: () => {
        controller.deleteContent(field._id);
      },
    });
  };

  const handleItemSave = async (content) => {
    // Ensure the type field is preserved from the original instance before formatter lookup
    if (!content.type && selectedInstance?.content?.type) {
      content.type = selectedInstance.content.type;
    }

    // Instance or global registered formatter
    const psf = onPreSaveFormatter || getPreSaveFormatter(content?.type);

    // See if there is a onPre save formatter specified
    content = psf ? psf(content) : content;

    // Note: Post-formatting is handled by ContentEditor.save() method automatically
    // No need to duplicate the formatting logic here

    if (content._id) delete content._id;

    // Build payload with only essential metadata fields
    // Avoid spreading entire selectedInstance to prevent corruption from stale/populated data
    const payload = {
      _id: selectedInstance._id,
      content,
    }

    // Preserve only specific metadata fields if they exist
    if (selectedInstance.localization) {
      payload.localization = selectedInstance.localization
    }
    if (selectedInstance.aldooCore !== undefined) {
      payload.aldooCore = selectedInstance.aldooCore
    }
    if (selectedInstance.deleted !== undefined) {
      payload.deleted = selectedInstance.deleted
    }
    if (selectedInstance.deletedAt) {
      payload.deletedAt = selectedInstance.deletedAt
    }

    const response = await controller.saveContent(payload);

    if (response.result) {
      const result = await controller.getContent({
        match: { _id: response.result },
      });
      
      setSelectedInstance(result.data[0]);
      // Refresh the DataTable to show the updated content
      setRefreshKey((prev) => prev + 1);
      return result.data[0];
    }
  };

  const refreshContent = async (contentId, retryCount = 0) => {
    // Add a delay to allow database to finish writing after download
    // Use exponential backoff: 300ms, 600ms, 1200ms
    if (retryCount > 0) {
      const delay = 300 * Math.pow(2, retryCount - 1);
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    const result = await controller.getContent({ match: { _id: contentId } });

    if (!result.data || result.data.length === 0) {
      console.error("No content found for ID:", contentId);
      return;
    }

    const fetchedItem = result.data[0];

    // Check if content has updatedAt field and compare with current
    if (selectedInstance && fetchedItem.updatedAt === selectedInstance.updatedAt && retryCount < 3) {
      return refreshContent(contentId, retryCount + 1);
    }

    // Just update selectedInstance - ContentEditor will detect the fieldValues change
    setSelectedInstance(fetchedItem);
  };

  const editItem = async (item) => {
    const result = await controller.getContent({ match: { _id: item._id } });

    if (!result.data || result.data.length === 0) {
      console.error("No content found for ID:", item._id);
      return;
    }

    const fetchedItem = result.data[0];
    setSelectedInstance(fetchedItem);

    const exists = getContentType(fetchedItem.content.type);
    if (exists) return;

    showPromptDialog({
      title: `Type ${fetchedItem.content.type} is not found`,
      message: `Would you like to try and open this content with different type?`,
      confirmText: "OK",
      onCancel: () => setSelectedInstance(null),
      onConfirm: () => setShowTypeSelector(true),
    });
  };

  const addNewContent = () => {
    // Get the actual type value from the contentType state
    let actualType = null;

    if (contentType && Array.isArray(contentType) && contentType.length) {
      actualType = contentType[0].value;
    } else if (externalContentType && typeof externalContentType === "string") {
      actualType = externalContentType;
    } else if (
      externalContentType &&
      Array.isArray(externalContentType) &&
      externalContentType.length
    ) {
      actualType =
        typeof externalContentType[0] === "object" &&
        externalContentType[0] !== null &&
        "value" in externalContentType[0]
          ? externalContentType[0].value
          : externalContentType[0];
    }

    if (actualType) {
      setSelectedInstance({
        content: { type: actualType },
      });
    } else {
      setShowTypeSelector(true);
    }

    // Call external close callback if provided
    if (onAddDialogClose) {
      onAddDialogClose();
    }
  };

  // Selection handlers
  const handleSelectContent = (item, isSelected) => {
    if (isSelected) {
      setSelectedContent([...selectedContent, item._id]);
      setSelectedContentData([...selectedContentData, item]);
    } else {
      setSelectedContent(selectedContent.filter(id => id !== item._id));
      setSelectedContentData(selectedContentData.filter(d => d._id !== item._id));
    }
  };

  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedContent(items.map(d => d._id));
      setSelectedContentData([...items]);
    } else {
      setSelectedContent([]);
      setSelectedContentData([]);
    }
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    showPromptDialog({
      title: "Delete Content",
      message: `Are you sure you want to delete ${selectedContent.length} selected item${selectedContent.length !== 1 ? 's' : ''}?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          await Promise.all(
            selectedContent.map(contentId =>
              controller.deleteContent(contentId)
            )
          );

          showNotification({
            message: `Successfully deleted ${selectedContent.length} item${selectedContent.length !== 1 ? 's' : ''}`,
            className: 'bg-admin_primary text-admin_text_inv',
          });

          setSelectedContent([]);
          setSelectedContentData([]);
          setRefreshKey((prev) => prev + 1);
        } catch (error) {
          console.error("Failed to delete content:", error);
          showNotification({
            message: "Failed to delete selected items",
            className: 'bg-admin_error text-white',
          });
        }
      }
    });
  };

  // Plugin handlers
  const handleRunPlugin = (pluginId) => {
    const plugin = pluginRegistry.get(pluginId);
    if (!plugin) {
      return;
    }

    // Filter out items with unregistered content types
    const validSelectedData = selectedContentData.filter(item => {
      const itemType = item?.content?.type;
      const hasDefinition = getContentType(itemType);
      return !!hasDefinition;
    });

    if (validSelectedData.length === 0) {
      showNotification({
        message: "Selected items have unregistered content types and cannot be processed by plugins",
        className: "bg-admin_error text-white",
      });
      return;
    }

    if (validSelectedData.length < selectedContentData.length) {
      showNotification({
        message: `${selectedContentData.length - validSelectedData.length} item(s) with unregistered types were excluded`,
        className: "bg-admin_warning text-white",
      });
    }

    // Get the current content type definition
    // If a specific content type is selected, use it
    // Otherwise, try to infer from selected items (they should all be the same type)
    let contentTypeValue = contentType?.[0]?.value || externalContentType;

    if (!contentTypeValue && validSelectedData.length > 0) {
      // Infer content type from first valid selected item
      contentTypeValue = validSelectedData[0]?.content?.type;
    }

    let currentContentType = getContentType(contentTypeValue);

    // Content type should exist since we filtered out invalid items
    if (!currentContentType && contentTypeValue && validSelectedData.length > 0) {

      // Infer field structure from first item's content
      const sampleContent = selectedContentData[0]?.content;
      const inferredFields = [];

      if (sampleContent) {
        Object.keys(sampleContent).forEach(key => {
          if (key === 'type') return; // Skip the type field itself

          const value = sampleContent[key];
          let fieldType = 'text';

          // Infer type from value
          if (typeof value === 'number') fieldType = 'number';
          else if (typeof value === 'boolean') fieldType = 'boolean';
          else if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) fieldType = 'date';
          else if (typeof value === 'string' && value.length > 100) fieldType = 'longtext';

          inferredFields.push({
            name: key,
            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
            type: fieldType
          });
        });
      }

      currentContentType = {
        type: contentTypeValue,
        type_definition: inferredFields
      };
    }

    // Create plugin context with only valid items
    const context = createPluginContext(
      validSelectedData,
      currentContentType,
      controller,
      {
        showDialog: (config) => {
          // Not used in current implementation
          return Promise.resolve({});
        },
        showNotification: (message, type) => {
          showNotification({
            message,
            className: type === 'error' ? 'bg-admin_error text-white' :
                      type === 'success' ? 'bg-admin_success text-white' :
                      'bg-admin_info text-white',
          });
        },
        showProgress: (current, total, message) => {
          setProgressData({ current, total, message });
          if (!showProgress && total > 0) {
            setShowProgress(true);
          }
        },
        confirm: (message) => {
          return showPromptDialog({
            title: "Confirm",
            message,
            confirmText: "Yes",
            cancelText: "No",
          });
        },
      }
    );

    // Validate plugin can run
    const validation = pluginRegistry.validate(pluginId, context);
    if (!validation.valid) {
      showNotification({
        message: validation.errors?.[0]?.message || "Cannot run plugin",
        className: "bg-admin_error text-white",
      });
      return;
    }

    // Show plugin dialog
    setActivePlugin({ plugin, context });
    setShowPluginDialog(true);
  };

  const handlePluginExecute = async (values) => {
    if (!activePlugin) return;

    try {
      const result = await pluginRegistry.run(
        activePlugin.plugin.id,
        activePlugin.context,
        { values }
      );

      // Close progress dialog
      setShowProgress(false);

      // Refresh the data table if items were modified
      if (result.itemsProcessed > 0) {
        setRefreshKey((prev) => prev + 1);
        setSelectedContent([]);
        setSelectedContentData([]);
      }

      return result;
    } catch (error) {
      setShowProgress(false);
      throw error;
    }
  };

  // Base column definitions
  const getBaseColumnDefinitions = () => ({
    edit: {
      id: "edit",
      headerClassName: "w-7 text-center",
      render: (_, item) => (
        <Button
          variant="admin_icon"
          className="text-admin_text_secondary mx-auto"
          onClick={() => editItem(item)}
        >
          <PencilIcon className="w-5 h-5" />
        </Button>
      ),
    },
    delete: {
      id: "delete",
      headerClassName: "w-6",
      render: (_, item) => (
        <Button
          variant="admin_icon"
          className="text-admin_text_secondary hover:text-admin_error"
          onClick={() => deleteInstanceAction(item)}
        >
          <TrashIcon className="w-5 h-5" />
        </Button>
      ),
    },
  });

  // Combine all columns
  const getTableColumns = () => {
    const baseColumnDefs = getBaseColumnDefinitions();
    const columns = baseColumns.map((id) => baseColumnDefs[id]);
    return [...additionalColumns, ...columns];
  };

  // Header controls
  const headerControls = (
    <div className="w-full flex items-end justify-between gap-3">
      <div className="flex gap-3 items-end">
        {allowTypeSelection && (
          <div className="flex flex-col -mt-7">
            <Typography variant="admin_txt2_dim" className="mb-0.5">
              Content Type
            </Typography>

            <DropDownSelect
              variant="admin"
              key={`type-dropdown-${refreshKey}`}
              options={resolveAvailableTypeNames()}
              multiSelection
              searchEnabled
              fixedWidth="235px"
              placeholder={
                availableTypeNames()?.length > 0
                  ? "Any Type"
                  : "No types loaded"
              }
              value={contentType}
              onChange={(value) => {
                // Check if this is a "deselect all" scenario or empty selection
                if (
                  !value ||
                  (Array.isArray(value) && value.length === 0) ||
                  hasInvalidValues(value)
                ) {
                  // Reset the content type to show all types
                  setContentType(null);
                } else {
                  // Normal selection update
                  setContentType(value);
                }

                // Always refresh the data table
                setRefreshKey((prev) => prev + 1);
              }}
            />
          </div>
        )}

        <DateFilterDropdown
          onChange={handleDateFilterChange}
          variant="admin"
          value={dateFilter}
          className="w-[190px] -mt-8"
          styleOverrides={{
            label: "text-admin_text_secondary text-xs font-normal mb-0.5",
            header: "w-full flex justify-between items-end gap-2 mb-1",
          }}
        />

        {/* Custom header controls */}
        {customHeaderControls && (
          <div className="flex gap-3 items-end">{customHeaderControls}</div>
        )}
      </div>
    </div>
  );

  const filterControl = (
    <FilterPanel
      value={customFilters}
      onChange={setCustomFilters}
      onApply={(value) => {
        setCustomFilters(value)
        setRefreshKey((prev) => prev + 1)
      }}
      onClear={() => {
        setCustomFilters([])
        setRefreshKey((prev) => prev + 1)
      }}
      dataModel={filterDataModel}
      selectedModelType={contentType}
    />
  );


  // Build request fields
  const getRequestFields = useMemo(() => {
    const typeFilter = getTypeFilter();
    const customFiltersMatch = buildCustomMatchFilters(customFilters);
    
    const match = {
      "content.type": typeFilter,
      deleted: { $ne: true },
      ...additionalFilters,
      ...customFiltersMatch,
    };

    // Handle date filter
    if (dateFilter) {
      if (dateFilter.type === "preset" && dateFilter.value) {
        // Preset date (e.g., last 24h, last 7d)
        const { days } = dateFilter.value;
        if (days) {
          // Use ISO string format that MongoDB can compare
          const cutoffDate = moment()
            .subtract(days, "days")
            .utc()
            .toISOString();
          match.createdAt = { $gte: cutoffDate };
        }
        // If dateFilter.value is null (lifetime option), no date restriction
      } else if (dateFilter.type === "custom" && dateFilter.range) {
        // Custom date range
        const { startDate, endDate } = dateFilter.range;
        if (startDate && endDate) {
          // Set the time of endDate to 23:59:59 to include the entire day
          const adjustedEndDate = moment(endDate)
            .utc()
            .endOf("day")
            .toISOString();
          const adjustedStartDate = moment(startDate)
            .utc()
            .startOf("day")
            .toISOString();
          match.createdAt = {
            $gte: adjustedStartDate,
            $lte: adjustedEndDate,
          };
        }
      }
    }

    return { 
      match,
      // Add cache buster to force fresh API calls
      _cacheBuster: Date.now()
    };
  }, [contentType, additionalFilters, dateFilter, customFilters, refreshKey, externalRefreshTrigger]);

  // Validation
  const validators = (errors) => ({
    id: async (value, item) => {
      if (item.id === undefined) return;
      if (!value) return (errors.id = "ID is required");
      if (!item._id) return;

      // Get type from item.type or fall back to contentType from the component
      const type = item.type || contentType;
      const { id, _id } = item;

      const result = await controller.getContent({
        match: {
          _id: { $ne: _id },
          deleted: { $ne: true },
          "content.type": type,
          "content.id": id,
        },
      });

      if (result?.totalItems > 0)
        errors.id = `${id} is already taken. Please enter a different ID.`;
    },
    ...(customValidators?.(errors) || {}),
  });

  return (
    <div className={styleOverrides.wrapper || "p-4"}>
      {/* Header */}
      {contentTitle && (
        <div className="w-full flex items-center justify-between mb-5">
          <Typography 
            component="h1"
            variant="admin_h3"
            className="ml-4"
          >
            {contentTitle}
          </Typography>

          <div className="flex gap-2 items-center">
            {showRestoreButton && (
              <Button
                variant="admin_secondary"
                className="w-32 whitespace-nowrap"
                onClick={() => setOpenRestoreDialog(true)}
              >
                Restore
              </Button>
            )}

            {addButtonText && (
              <Button
                variant="admin_primary"
                className="whitespace-nowrap"
                onClick={addNewContent}
              >
                <div className="flex items-center gap-1">
                  <PlusCircleIcon className="w-5 h-5" />
                  {addButtonText}
                </div>
              </Button>
            )}
          </div>
        </div>
      )}

      <DataTable
        variant="admin_browser"
        apiMethod={GetCMSContent}
        preferredHeight="75vh"
        headerControls={headerControls}
        filterControl={filterControl}
        itemHeight="h-[50px]"
        searchField={searchFields}
        searchObject={{}}
        initialSearchTerm={searchTerm}
        onSearchTermChange={(term) => {
          setSearchTerm(term);
          if (onSearchChange) {
            onSearchChange(term);
          }
        }}
        requestFields={getRequestFields}
        sort={sort}
        renders={getTableColumns()}
        enableSelection={true}
        selectedItems={selectedContent}
        selectedData={selectedContentData}
        onSelectionChange={(ids, data) => {
          setSelectedContent(ids);
          setSelectedContentData(data);
        }}
        tableFooter={
          selectedContent.length > 0 ? (
            <div className="flex items-center justify-between p-4 border-t border-admin_bordered">
              <Typography variant="admin_body">
                {selectedContent.length} item{selectedContent.length !== 1 ? 's' : ''} selected
              </Typography>
              <div className="flex items-center gap-3">
                <DropDownSelect
                  options={pluginRegistry.getAll().map(plugin => ({
                    value: plugin.id,
                    label: plugin.name,
                    name: plugin.name,
                    description: plugin.description
                  }))}
                  placeholder="Actions"
                  variant="admin"
                  searchEnabled={true}
                  nameField="name"
                  onChange={(selected) => {
                    // DropdownSelect returns an array, get the first item
                    const selectedItem = Array.isArray(selected) ? selected[0] : selected;
                    if (selectedItem && selectedItem.value) {
                      handleRunPlugin(selectedItem.value);
                    }
                  }}
                  renderOption={(option) => (
                    <Tooltip text={option.description} position="left" variant="dark">
                      <Typography variant="admin_body" className="text-sm">
                        {option.label}
                      </Typography>
                    </Tooltip>
                  )}
                  fixedWidth="200px"
                  className="min-w-[200px]"
                />
                <Button
                  variant="admin_primary"
                  onClick={() => setShowInstallDialog(true)}
                >
                  <div className="flex items-center gap-1">
                    <CloudArrowUpIcon className="w-4 h-4" />
                    Install on Environment
                  </div>
                </Button>
                <Button
                  variant="admin_secondary"
                  onClick={() => {
                    setSelectedContent([]);
                    setSelectedContentData([]);
                  }}
                >
                  Deselect
                </Button>
                <Button
                  variant="admin_error"
                  onClick={handleBulkDelete}
                >
                  <div className="flex items-center gap-1">
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </div>
                </Button>
              </div>
            </div>
          ) : undefined
        }
        itemIdField="_id"
        styleOverrides={{
          table: "w-full table-fixed",
          searchInput: "absolute top-6 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          filterControl: "absolute top-6 right-[370px]",
          ...styleOverrides
        }}
        refreshKey={refreshKey}
        localUserPreferencesKey={localUserPreferencesKey}
        onDataLoaded={(rawData) => {
          return rawData?.map((item) => {
            item.onEdit = editItem;
            return item;
          });
        }}
        noData={
          <Typography variant="admin_h3">
            No content added yet
          </Typography>
        }
      />

      <Modal
        isOpen={
          selectedInstance !== null &&
          getContentType(selectedInstance?.content?.type)
        }
        onClose={() => setSelectedInstance(null)}
        variant="admin"
        className="w-full sm:w-[500px] md:w-[85%] md:absolute md:top-[15px] h-full md:h-auto"
      >
        {selectedInstance?.content?.type && (
          <ContentEditor
            title={previewTitle(selectedInstance)}
            allowContentImport
            validators={validators}
            contentType={getContentType(selectedInstance?.content?.type)}
            dataItem={selectedInstance}
            fieldValues={{
              ...defaultFieldValues,
              ...getDefaultFieldValues(getContentType(selectedInstance?.content?.type)),
              ...(selectedInstance?.content ? (() => {
                const { type, ...contentFields } = selectedInstance.content;
                return contentFields;
              })() : {}),
              _id: selectedInstance?._id,
            }}
            onError={(errors) => {
              showNotification({
                message: Object.values(errors).find((e) => e),
                className: "bg-admin_error text-white",
              });
            }}
            onSave={handleItemSave}
            onClickSave={(savedItem) => {
              // For newly created items (no _id before save), keep modal open so
              // all editor buttons (history, export, install) become visible with the new _id.
              // handleItemSave already called setSelectedInstance(fullItem) with the _id.
              if (!selectedInstance?._id && savedItem?._id) return
              setSelectedInstance(null)
            }}
            onRefresh={() => refreshContent(selectedInstance._id)}
          />
        )}
      </Modal>

      <TypeSelectorDialog
        isOpen={showTypeSelector}
        onClose={() => setShowTypeSelector(false)}
        onSelect={(type) => {
          if (selectedInstance) {
            selectedInstance.content.type = type.type;
            setSelectedInstance({ ...selectedInstance });
            return;
          }
          setSelectedInstance({
            content: { type: type.type },
          });
        }}
      />

      <RestoreDialog
        isOpen={openRestoreDialog}
        onClose={() => setOpenRestoreDialog(false)}
        type="content"
        controller={controller}
        additionalFilters={additionalFilters}
      />

      {showPreview && (
        <ImagePreview
          isOpen={isPreviewOpen}
          imageUrl={currentPreview}
          fileName={currentPreview ? filename(currentPreview) : ""}
          onClose={() => {
            setIsPreviewOpen(false);
            setCurrentPreview(null);
          }}
        />
      )}

      {activePlugin && (
        <PluginDialog
          plugin={activePlugin.plugin}
          context={activePlugin.context}
          isOpen={showPluginDialog}
          onClose={() => {
            setShowPluginDialog(false);
            setActivePlugin(null);
          }}
          onExecute={handlePluginExecute}
        />
      )}

      <ProgressDialog
        isOpen={showProgress}
        title="Processing Items"
        message={progressData.message}
        progress={progressData.total > 0 ? progressData.current / progressData.total : 0}
        onCancel={() => setShowProgress(false)}
        onClose={() => setShowProgress(false)}
      />

      {showInstallDialog && (
        <EnvironmentContentInstallDialog
          target={selectedContentData.map(item => ({
            type: "CMSContent",
            match: { "content.type": item.content.type, "_id": item._id }
          }))}
          contentName={`Selection (${selectedContentData.length} CMS item${selectedContentData.length !== 1 ? 's' : ''})`}
          onClose={() => setShowInstallDialog(false)}
        />
      )}
    </div>
  );
}
